import { CompetitionStage, Match, TournamentEvent, TournamentParticipant } from "../../../types";
import { minutesOfDay, toLocalDateKey } from "../../../utils/datetime";

// Logica pura del calendario globale: nessuna dipendenza da React, quindi
// testabile in isolamento. Il componente la richiama dentro useMemo.

export const PIXELS_PER_MINUTE = 0.9;
// Altezza minima della card: deve bastare a mostrare orario + giocatori +
// sottotitolo ("Torneo · Stage") senza che `overflow-hidden` li tronchi.
export const MIN_CARD_HEIGHT = 84;
// Altezza minima/massima di una "riga slot": la minima garantisce card leggibili
// (allarga la timeline quando gli slot sono ravvicinati), la massima comprime i
// lunghi vuoti tra slot.
export const MIN_ROW_HEIGHT = 92;
export const MAX_ROW_HEIGHT = 150;

export interface ScheduledMatch {
  match: Match;
  start: Date;
  end: Date;
  dayKey: string;
}

export interface PlacedMatch extends ScheduledMatch {
  lane: number; // corsia (colonna) all'interno dell'evento
  lanes: number; // numero totale di corsie nel cluster di sovrapposizione
}

// Tipo di conflitto evidenziato dal badge: stesso partecipante impegnato altrove
// ("player") oppure sovrapposizione nello stesso girone/stage ("group").
export type ConflictKind = "player" | "group";

export interface EventColumn {
  eventId: number;
  event: TournamentEvent | undefined;
  matches: PlacedMatch[];
  lanes: number;
}

export interface SlotLayout {
  slots: { minutes: number; top: number }[];
  yOf: (minutes: number) => number;
  totalHeight: number;
  endMinutesOf: (item: ScheduledMatch) => number;
}

export const formatScoreLabel = (match: Match): string =>
  match.score1 !== null && match.score2 !== null ? `${match.score1} - ${match.score2}` : "__ - __";

export const overlapsInTime = (a: ScheduledMatch, b: ScheduledMatch): boolean =>
  a.start.getTime() < b.end.getTime() && a.end.getTime() > b.start.getTime();

// Due match sovrapposti sono in conflitto solo se NON appartengono a gironi
// distinti: gironi diversi possono giocarsi in contemporanea, mentre lo stesso
// girone (o uno stage senza gironi, es. eliminatorie) no.
export const isConflict = (a: ScheduledMatch, b: ScheduledMatch): boolean =>
  overlapsInTime(a, b) &&
  (a.match.group == null || b.match.group == null || a.match.group === b.match.group);

export const conflictBadgeInfo = (kinds: Set<ConflictKind>): { label: string; title: string } => {
  const hasPlayer = kinds.has("player");
  const hasGroup = kinds.has("group");
  if (hasPlayer && hasGroup) {
    return {
      label: "conflitto",
      title: "Stesso partecipante e sovrapposizione nello stesso girone/stage",
    };
  }
  if (hasPlayer) {
    return {
      label: "giocatore",
      title: "Stesso partecipante impegnato in un altro match in contemporanea",
    };
  }
  return {
    label: "girone",
    title: "Sovrapposizione con un altro match dello stesso girone/stage",
  };
};

// Assegna a ogni match una "corsia" così che i match sovrapposti finiscano in
// colonne affiancate (interval partitioning) invece di accavallarsi: utile per
// gli stage a gironi dove più partite sono legittimamente in contemporanea.
export const placeInLanes = (items: ScheduledMatch[]): PlacedMatch[] => {
  const sorted = [...items].sort(
    (a, b) => a.start.getTime() - b.start.getTime() || a.end.getTime() - b.end.getTime()
  );
  const placed: PlacedMatch[] = [];
  let cluster: PlacedMatch[] = [];
  let laneEnds: number[] = []; // istante di fine dell'ultimo match per ogni corsia
  let clusterEnd = 0;

  const flush = () => {
    cluster.forEach((entry) => (entry.lanes = laneEnds.length));
    cluster = [];
    laneEnds = [];
    clusterEnd = 0;
  };

  sorted.forEach((item) => {
    const startMs = item.start.getTime();
    if (cluster.length && startMs >= clusterEnd) flush();
    let lane = laneEnds.findIndex((end) => end <= startMs);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(item.end.getTime());
    } else {
      laneEnds[lane] = item.end.getTime();
    }
    const entry: PlacedMatch = { ...item, lane, lanes: 1 };
    cluster.push(entry);
    placed.push(entry);
    clusterEnd = Math.max(clusterEnd, item.end.getTime());
  });
  flush();
  return placed;
};

// Da match grezzi a match programmati (con start/end/dayKey), scartando quelli
// senza data valida e ordinandoli per orario di inizio.
export const buildScheduledMatches = (matches: Match[]): ScheduledMatch[] =>
  matches
    .map((match) => {
      const start = match.scheduled_at ? new Date(match.scheduled_at) : null;
      if (!start || Number.isNaN(start.getTime())) return null;
      const duration = match.duration_minutes || 60;
      const end = new Date(start.getTime() + duration * 60 * 1000);
      return { match, start, end, dayKey: toLocalDateKey(start) };
    })
    .filter((item): item is ScheduledMatch => item !== null)
    .sort((a, b) => a.start.getTime() - b.start.getTime());

// Mappa matchId -> tipi di conflitto, valutando tutte le coppie del giorno.
export const computeConflicts = (
  dayMatches: ScheduledMatch[],
  stagesById: Map<number, CompetitionStage>,
  participantsById: Map<number, TournamentParticipant>
): Map<number, Set<ConflictKind>> => {
  const map = new Map<number, Set<ConflictKind>>();
  const mark = (matchId: number, kind: ConflictKind) => {
    const kinds = map.get(matchId) ?? new Set<ConflictKind>();
    kinds.add(kind);
    map.set(matchId, kinds);
  };
  const usersOf = (match: Match): number[] => {
    const result: number[] = [];
    [match.player1, match.player2].forEach((participantId) => {
      if (participantId == null) return;
      participantsById.get(participantId)?.players.forEach((userId) => result.push(userId));
    });
    return result;
  };
  for (let i = 0; i < dayMatches.length; i += 1) {
    for (let j = i + 1; j < dayMatches.length; j += 1) {
      const a = dayMatches[i];
      const b = dayMatches[j];
      if (!overlapsInTime(a, b)) continue;
      const stageA = stagesById.get(a.match.stage);
      const stageB = stagesById.get(b.match.stage);
      const sameEvent = !!stageA && !!stageB && stageA.event === stageB.event;
      if (sameEvent && isConflict(a, b)) {
        mark(a.match.id, "group");
        mark(b.match.id, "group");
      }
      if (usersOf(a.match).some((userId) => usersOf(b.match).includes(userId))) {
        mark(a.match.id, "player");
        mark(b.match.id, "player");
      }
    }
  }
  return map;
};

// Raggruppa i match del giorno per evento (Torneo), assegna le corsie e ordina
// le colonne per orario del primo match.
export const buildEventColumns = (
  dayMatches: ScheduledMatch[],
  stagesById: Map<number, CompetitionStage>,
  eventsById: Map<number, TournamentEvent>
): EventColumn[] => {
  const map = new Map<number, ScheduledMatch[]>();
  dayMatches.forEach((item) => {
    const stage = stagesById.get(item.match.stage);
    if (!stage) return;
    map.set(stage.event, [...(map.get(stage.event) ?? []), item]);
  });
  return Array.from(map.entries())
    .map(([eventId, eventMatches]) => {
      const placedMatches = placeInLanes(eventMatches);
      return {
        eventId,
        event: eventsById.get(eventId),
        matches: placedMatches,
        lanes: Math.max(1, ...placedMatches.map((entry) => entry.lanes)),
      };
    })
    .sort((a, b) => {
      const firstA = a.matches[0]?.start.getTime() ?? 0;
      const firstB = b.matches[0]?.start.getTime() ?? 0;
      if (firstA !== firstB) return firstA - firstB;
      return (a.event?.tournament.name ?? "").localeCompare(b.event?.tournament.name ?? "");
    });
};

// Layout a "slot": ogni riga è un orario di inizio distinto tra tutti i tornei
// del giorno. Le righe sono spaziate in base al gap reale verso lo slot
// successivo, ma con un minimo (leggibilità) e un massimo (i vuoti lunghi non
// sprecano spazio). yOf() mappa un minuto del giorno alla coordinata verticale,
// così una card finisce dentro la propria riga finché la sua durata non sfora
// l'inizio dello slot successivo: solo in quel caso la card si sovrappone.
export const buildSlotLayout = (dayMatches: ScheduledMatch[]): SlotLayout => {
  const endMinutesOf = (item: ScheduledMatch) => {
    const startMin = minutesOfDay(item.start);
    const endMin = minutesOfDay(item.end);
    return endMin > startMin ? endMin : startMin + (item.match.duration_minutes || 60);
  };

  if (!dayMatches.length) {
    return { slots: [], yOf: () => 0, totalHeight: 360, endMinutesOf };
  }

  const startMinutes = Array.from(new Set(dayMatches.map((item) => minutesOfDay(item.start)))).sort(
    (a, b) => a - b
  );

  let acc = 0;
  const slots = startMinutes.map((minutes, index) => {
    const next = startMinutes[index + 1];
    let gap: number;
    if (next != null) {
      gap = next - minutes;
    } else {
      const durations = dayMatches
        .filter((item) => minutesOfDay(item.start) === minutes)
        .map((item) => item.match.duration_minutes || 60);
      gap = Math.max(60, ...durations);
    }
    const height = Math.min(MAX_ROW_HEIGHT, Math.max(MIN_ROW_HEIGHT, gap * PIXELS_PER_MINUTE));
    const top = acc;
    acc += height;
    return { minutes, top, height, scale: height / gap };
  });

  const yOf = (minutes: number) => {
    if (minutes <= slots[0].minutes) return slots[0].top;
    for (let i = 0; i < slots.length; i += 1) {
      const nextMin = slots[i + 1]?.minutes;
      if (nextMin == null || minutes < nextMin) {
        return slots[i].top + (minutes - slots[i].minutes) * slots[i].scale;
      }
    }
    const last = slots[slots.length - 1];
    return last.top + (minutes - last.minutes) * last.scale;
  };

  const totalHeight = dayMatches.reduce((max, item) => Math.max(max, yOf(endMinutesOf(item))), acc);

  return {
    slots: slots.map(({ minutes, top }) => ({ minutes, top })),
    yOf,
    totalHeight: Math.max(360, totalHeight),
    endMinutesOf,
  };
};

// Una colonna con più corsie (gironi in contemporanea) deve essere più larga
// così ogni card resta leggibile.
export const columnWidthPx = (lanes: number): number => Math.max(220, lanes * 220);
